// @flow
import { connect } from 'react-redux'
import type { Store } from '../../redux/ducks'
import EditForm, { type Props } from './EditForm'
import {
  emptyCommonErrors,
  emptyMediaErrors,
  patch,
  mediaAdd,
  commonPatch,
  commonErrorPatch,
  mediaPatch,
  mediaErrorPatch,
  advertisementTemplateErrorPatch
} from '../../redux/ducks/advertisementTemplate/reducers'
import {
  put as audienceSelectorPut,
  patch as audienceSelectorPatch
} from '../../redux/ducks/audiencesSelector/reducers'
import type {
  ProcessState,
  FieldsState,
  MediaState,
  CommonFields,
  CommonErrors,
  AdvertisementTemplateErrors,
  AdvertisementTemplateAudienceData
} from '../../redux/ducks/advertisementTemplate/reducers'
import _ from 'lodash'
import postJson from '../../services/postJson'
import type { Initial } from './EditForm'
import type { ControlVisibility } from '../AdEdit'
import store from '../../redux/store'
import type { RequestPayloadItem } from '../prepareProviderTemplateBlocks'
import { adaptRawAdvertisementTemplate } from '../../redux/ducks/AdvertisementDataAdapter'
import type { Configs, Crop } from '../types'
import { Audience } from '../constants'
import { prepareTplProviderTemplateBlocks } from '../prepareProviderTemplateBlocks'
import {
  processAdvertisementTemplateCommonErrors,
  processAdvertisementTemplateMediaErrors,
  type ErrorsResponseData
} from '../initErrorsFromResponse'
import { putErrors } from '../../redux/ducks/carouselEdit/operations'
import { hasCarouselAnyErrors } from '../../redux/selectors'
import { controlVisibilityFromList } from '../connectProviderTemplatesWithControls'

type AdvertisementTemplatePayload = {|
  status: string,
  name: string,
  audience_ids: Array<number>,
  provider_templates: RequestPayloadItem
|}

type OwnProps = {|
  action: 'copy' | 'update' | 'create',
  configs: Configs,
  hideAudiences: boolean
|}

function providerTemplateNamesToFill(storeState: Store): Array<string> {
  const data = storeState.advertisementTemplate

  if (!data) return []
  const selectedAudiencesLists = _.map(data.audiences, (a) =>
    _.includes(storeState.audiencesSelector.ids, a.id) ? a.list : []
  )

  return _.sortBy(
    _.uniq(_.flattenDeep([data.initialProviderTemplateNames, selectedAudiencesLists]))
  )
}

function controlVisibility(storeState: Store): ControlVisibility {
  const list = providerTemplateNamesToFill(storeState)

  return controlVisibilityFromList(list)
}

function isAnyErrors(state: Store) {
  return (
    isAnyCommonErrors(state) ||
    isAnyMediaErrors(state) ||
    isAnyAdvertisementTemplateErrors(state) ||
    hasCarouselAnyErrors(state, state.advertisementTemplate.common.carouselStoreId)
  )
}

function isAnyCommonErrors(state: Store) {
  return !_.every(_.values(state.advertisementTemplate.commonErrors), _.isEmpty)
}

function isAnyAdvertisementTemplateErrors(state: Store) {
  return !_.every(_.values(state.advertisementTemplate.advertisementTemplateErrors), _.isEmpty)
}

function isAnyMediaErrors(state: Store) {
  const data = state.advertisementTemplate.mediaErrors

  return _.some(
    _.map(data, (errors) => {
      return !_.every(_.values(errors || {}), _.isEmpty)
    })
  )
}

function requestBody(
  store: Store,
  media: MediaState,
  status: string
): AdvertisementTemplatePayload {
  const audiencesSelector = store.audiencesSelector
  return {
    status: status,
    name: store.advertisementTemplate.common.name,
    audience_ids: audiencesSelector.ids,
    provider_templates: _.pick(
      prepareTplProviderTemplateBlocks(store, store.advertisementTemplate.common, media),
      providerTemplateNamesToFill(store)
    )
  }
}

function mapStateToProps(storeState: Store, ownProps: OwnProps) {
  const state = storeState.advertisementTemplate
  const audiencesSelector = storeState.audiencesSelector
  const audiences = state.audiences
  const selectedAudienceIds = audiencesSelector.ids
  return {
    data: state,
    initialSelectedAudienceIds: state.initialSelectedAudienceIds,
    selectedAudienceIds: selectedAudienceIds,
    isLoading: state.isLoading,
    controlVisibility: controlVisibility(storeState),
    audiences: audiences,
    urlCheck: state.urlCheck,
    allowMultiUpload: state.media.length <= 1 && ownProps.action !== 'update',
    isAnyErrors: isAnyErrors(storeState),
    vkOptionsWithoutPublic: areVkLinkButtonOptionsWithoutPublic(audiences, selectedAudienceIds),
    fbPromotionPosts: state.fbPromotionPosts
  }
}

function areVkLinkButtonOptionsWithoutPublic(
  audiences: Array<AdvertisementTemplateAudienceData>,
  selectedAudienceIds: Array<number>
) {
  const vkPlacements = _.compact(
    _.map(audiences, (audience) => {
      if (_.includes(selectedAudienceIds, audience.id)) return audience.vkPlacement
    })
  )
  return _.includes(vkPlacements, Audience.vk_placements.promopost_without_public)
}

function mapDispatchToProps(dispatch: Function, ownProps: OwnProps) {
  const changeCommon = (diff: $Shape<CommonFields>) => dispatch(commonPatch(diff))
  const changeErrors = (diff: $Shape<CommonErrors>) => dispatch(commonErrorPatch(diff))
  const changeTemplateErrors = (diff: $Shape<AdvertisementTemplateErrors>) =>
    dispatch(advertisementTemplateErrorPatch(diff))
  return {
    onMediaAdd: () => dispatch(mediaAdd()),
    onAudienceIdsChange: (ids: Array<number>) => {
      dispatch(audienceSelectorPatch({ ids }))
    },
    onPersist: (status: string, callbacks: {| onSuccess: () => void, onFailure: () => void |}) => {
      dispatch(patch({ isLoading: true }))
      const state: Store = store.getState()
      const templatesMedia = state.advertisementTemplate.media
      const requestUrl = _.includes(['create', 'copy'], ownProps.action)
        ? '/api/v1/advertisement_templates'
        : `/api/v1/advertisement_templates/${state.advertisementTemplate.common.id ||
            'undefinedID'}`
      const requestBodyArray = _.map(templatesMedia, (media) => {
        return requestBody(state, media, status)
      })
      postJson(requestUrl, { data: { items: requestBodyArray } })
        .done(() => {
          dispatch(commonErrorPatch(emptyCommonErrors))
          _.each(templatesMedia, (media, index) => {
            dispatch(mediaErrorPatch(index, emptyMediaErrors))
          })
          dispatch(audienceSelectorPatch({ errors: [] }))
          callbacks.onSuccess()
        })
        .fail((response: any) => {
          if (response.status === 500) {
            window.alert('Internal Server Error. Contact support')
          } else {
            const errorsResponse: Array<ErrorsResponseData> = response.responseJSON.message.items
            dispatch(audienceSelectorPatch({ errors: _.first(errorsResponse).audience_ids || [] }))
            const processedErrors = processAdvertisementTemplateCommonErrors(
              _.first(errorsResponse)
            )
            dispatch(commonErrorPatch(processedErrors.commonErrors))
            dispatch(advertisementTemplateErrorPatch(processedErrors.advertisementTemplateErrors))
            const carouselStoreId = state.advertisementTemplate.common.carouselStoreId
            if (carouselStoreId) {
              putErrors(carouselStoreId, processedErrors.carouselErrors)
            }
            _.each(errorsResponse, (errors, index) => {
              dispatch(
                mediaErrorPatch(
                  index,
                  processAdvertisementTemplateMediaErrors(errors.provider_templates || {})
                )
              )
            })
          }
          callbacks.onFailure()
        })
        .always(() => {
          dispatch(patch({ isLoading: false }))
        })
    },
    onNameChange: (val: string) => {
      changeCommon({ name: val })
      changeTemplateErrors({ nameErrors: [] })
    },
    onUrlChange: (val: string) => {
      changeCommon({ url: val })
      changeErrors({ urlErrors: [] })
    },
    onTitleChange: (val: string) => {
      changeCommon({ title: val })
      changeErrors({ titleErrors: [] })
    },
    onProductTitleChange: (val: string) => {
      changeCommon({ productTitle: val })
      changeErrors({ productTitleErrors: [] })
    },
    onColumnDescriptionChange: (val: string) => {
      changeCommon({ columnDescription: val })
      changeErrors({ columnDescriptionErrors: [] })
    },
    onMultiDescriptionChange: (val: string) => {
      changeCommon({ multiDescription: val })
      changeErrors({ multiDescriptionErrors: [] })
    },
    onProductDescriptionChange: (val: string) => {
      changeCommon({ productDescription: val })
      changeErrors({ productDescriptionErrors: [] })
    },
    onLogoImageChange: (storeId: string) => {
      changeCommon({ logoImageStoreId: storeId, logoImageCrop: null })
      changeErrors({ logoImageErrors: [] })
    },
    onLogoImageCropChange: (crop: Crop) => changeCommon({ logoImageCrop: crop }),
    onSnippetImageChange: (storeId: string) => {
      changeCommon({ snippetImageStoreId: storeId, snippetImageCrop: null })
      changeErrors({ snippetImageErrors: [] })
    },
    onSnippetImageCropChange: (crop: Crop) => changeCommon({ snippetImageCrop: crop }),
    onVkUrlChange: (val: string) => {
      changeCommon({ vkUrl: val })
      changeErrors({ vkUrlErrors: [] })
    },
    onVkTitleChange: (val: string) => {
      changeCommon({ vkTitle: val })
      changeErrors({ vkTitleErrors: [] })
    },
    onVkMultiDescriptionChange: (val: string) => {
      changeCommon({ vkMultiDescription: val })
      changeErrors({ vkMultiDescriptionErrors: [] })
    },
    onVkWithoutButtonDescriptionChange: (val: string) => {
      changeCommon({ vkWithoutButtonDescription: val })
      changeErrors({ vkWithoutButtonDescriptionErrors: [] })
    },
    onVkLinkTitleChange: (val: string) => {
      changeCommon({ vkLinkTitle: val })
      changeErrors({ vkLinkTitleErrors: [] })
    },
    onVkLinkButtonChange: (val: string) => {
      changeCommon({ vkLinkButton: val })
      changeErrors({ vkLinkButtonErrors: [] })
    },
    onTmCarouselDescriptionChange: (val: string) => {
      changeCommon({ tmCarouselDescription: val })
      changeErrors({ tmCarouselDescriptionErrors: [] })
    },
    onTmLinkButtonChange: (val: string) => {
      changeCommon({ tmLinkButton: val })
      changeErrors({ tmLinkButtonErrors: [] })
    },
    onTmUrlChange: (val: string) => {
      changeCommon({ tmUrl: val })
      changeErrors({ tmUrlErrors: [] })
    },
    onFbUrlChange: (val: string) => {
      changeCommon({ fbUrl: val })
      changeErrors({ fbUrlErrors: [] })
    },
    onFbTitleChange: (val: string) => {
      changeCommon({ fbTitle: val })
      changeErrors({ fbTitleErrors: [] })
    },
    onFbMultiDescriptionChange: (val: string) => {
      changeCommon({ fbMultiDescription: val })
      changeErrors({ fbMultiDescriptionErrors: [] })
    },
    onFbLinkButtonChange: (val: string) => {
      changeCommon({ fbLinkButton: val })
      changeErrors({ fbLinkButtonErrors: [] })
    }
  }
}

export function initialize(dispatch: Function, data: Initial, extraAudienceIds: Array<number>) {
  const audienceIds = data.audiences.map((a) => a.id)
  const selectedAudienceIds = _.uniq(
    _.concat(
      _.filter(
        _.map(data.template_with_relations.advertisements, (ad) => ad.audience_id),
        (id) => _.includes(audienceIds, id)
      ),
      extraAudienceIds
    )
  )
  const processDiff: ProcessState = {
    editFormCopyPath: data.edit_form_copy_path,
    initialProviderTemplateNames: _.keys(data.template_with_relations.provider_templates),
    initialSelectedAudienceIds: selectedAudienceIds,
    audiences: data.audiences,
    isLoading: false,
    urlCheck: data.template_with_relations.url_check,
    fbPromotionPosts: []
  }
  const fieldsDiff: FieldsState = adaptRawAdvertisementTemplate(
    data.id,
    data.template_with_relations
  )
  dispatch(
    audienceSelectorPut({
      options: data.audience_options,
      ids: selectedAudienceIds,
      errors: []
    })
  )
  dispatch(patch({ ...processDiff }))
  dispatch(commonPatch({ ...fieldsDiff.common }))
  _.each(fieldsDiff.media, (media, index) => {
    dispatch(mediaPatch(index, { ...media }))
  })
  const postsPromotionAudienceIds = _.map(
    _.filter(data.audiences, (audience) => {
      return audience.audienceType === Audience.types.posts_promotion
    }),
    'id'
  )
  postJson('/advertisements/fb_published_posts', { audience_ids: postsPromotionAudienceIds }).done(
    (response) => {
      dispatch(patch({ fbPromotionPosts: response.result }))
    }
  )
}

const EditFormContainer = connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(EditForm)

export default EditFormContainer
